import { createContext, useCallback, useContext, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ApiError, type ApiClient } from '@despachador/api-client';
import { Field, Modal } from '../index';
import { goToGoogle, saveReturnPath } from './google-return';
import { authMessage, CodeInput, errorCode } from './pieces';

type Guard = <T>(fn: () => Promise<T>) => Promise<T>;
const ReauthCtx = createContext<Guard>((fn) => fn());

/** Code of the error a guarded call throws when the person closes the dialog. */
export const REAUTH_CANCELLED = 'REAUTH_CANCELLED';

/**
 * Wraps sensitive calls: when the API answers REAUTH_REQUIRED the person confirms their password
 * (and second factor) in a dialog (`POST /auth/reauth`) and the call is retried once.
 * `const guard = useReauth(); await guard(() => api.post(...))`.
 *
 * With a linked Google account (and sign-in with Google switched on) the dialog also offers
 * «Confirmar con Google»: a full navigation, so the pending call cannot be resumed; the person comes
 * back to `/acceso/google#reauth=…`, confirms and repeats the action. `allowGoogle={false}` where
 * leaving the page would lose something (the Google landing itself).
 */
export function ReauthProvider({ api, children, allowGoogle = true }: { api: ApiClient; children: ReactNode; allowGoogle?: boolean }) {
  const [pending, setPending] = useState<{ resolve: (ok: boolean) => void } | null>(null);
  const guard: Guard = useCallback(async (fn) => {
    try {
      return await fn();
    } catch (err) {
      if (errorCode(err) !== 'REAUTH_REQUIRED') throw err;
      const ok = await new Promise<boolean>((resolve) => setPending({ resolve }));
      if (!ok) throw new ApiError(403, REAUTH_CANCELLED, 'Operación cancelada.');
      return fn();
    }
  }, []);
  return (
    <ReauthCtx.Provider value={guard}>
      {children}
      {pending && (
        <ReauthDialog
          api={api}
          allowGoogle={allowGoogle}
          onClose={(ok) => {
            pending.resolve(ok);
            setPending(null);
          }}
        />
      )}
    </ReauthCtx.Provider>
  );
}

export const useReauth = () => useContext(ReauthCtx);

function ReauthDialog({ api, allowGoogle, onClose }: { api: ApiClient; allowGoogle: boolean; onClose: (ok: boolean) => void }) {
  const [password, setPassword] = useState('');
  const [value, setValue] = useState('');
  const [needCode, setNeedCode] = useState(false);
  const [recovery, setRecovery] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** Offer Google only when this account has one linked and the company has it switched on. */
  const [google, setGoogle] = useState(false);
  useEffect(() => {
    if (!allowGoogle) return;
    let live = true;
    api.account.google.status().then(
      (s) => live && setGoogle(s.available && s.linked),
      () => undefined, // the password is always there
    );
    return () => {
      live = false;
    };
  }, [api, allowGoogle]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.account.reauth({ password, ...(needCode ? (recovery ? { recoveryCode: value.trim() } : { code: value }) : {}) });
      onClose(true);
    } catch (err) {
      if (errorCode(err) === 'MFA_REQUIRED') setNeedCode(true);
      else setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };
  const withGoogle = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.account.google.reauth();
      saveReturnPath(`${window.location.pathname}${window.location.search}`);
      goToGoogle(url);
    } catch (err) {
      setError(authMessage(err));
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Confirma que eres tú"
      onClose={() => onClose(false)}
      footer={
        <>
          <button type="button" className="btn" onClick={() => onClose(false)}>
            Cancelar
          </button>
          <button type="submit" form="reauth-form" className="btn primary" disabled={busy}>
            {busy ? 'Confirmando…' : 'Confirmar'}
          </button>
        </>
      }
    >
      <form id="reauth-form" className="stack" onSubmit={(e) => void submit(e)}>
        <p className="small" style={{ margin: 0 }}>
          Por seguridad, esta acción pide tu contraseña otra vez.
        </p>
        <Field label="Tu contraseña">
          <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required autoFocus />
        </Field>
        {needCode && (
          <>
            <CodeInput value={value} onChange={setValue} recovery={recovery} autoFocus />
            <button
              type="button"
              className="btn ghost sm"
              onClick={() => {
                setRecovery(!recovery);
                setValue('');
              }}
            >
              {recovery ? 'Usar el código de la app' : 'Usar un código de recuperación'}
            </button>
          </>
        )}
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
        {google && (
          <div className="stack" style={{ gap: 'var(--space-2)' }}>
            <p className="small muted" style={{ margin: 0 }}>
              O confirma con tu cuenta de Google. Saldrás un momento de esta pantalla: al volver tendrás que repetir la acción.
            </p>
            <button type="button" className="btn block" disabled={busy} onClick={() => void withGoogle()}>
              Confirmar con Google
            </button>
          </div>
        )}
      </form>
    </Modal>
  );
}
